/**
 * Booking Flow Guide
 * How to create and manage bookings
 * Updated to reflect Feature 009: Workshop Booking & Job-Tracking System
 */

import {
  Calendar,
  UserPlus,
  Car,
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  XCircle,
  ArrowRight,
  Wrench,
  Users,
  Shield,
  BadgeCheck,
} from 'lucide-react';
import { pageStyles, cardStyles } from '../../lib/styles';

export function BookingFlow() {
  return (
    <div className={pageStyles.container}>
      {/* Introduction */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <div className="flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5 text-blue-500" />
            <span className="text-xs font-medium text-gray-900">Gestion de Citas</span>
          </div>
        </div>
        <div className={`${cardStyles.content} prose prose-gray max-w-none`}>
          <p>
            Las citas son el punto de entrada principal para los trabajos del
            taller. Una cita representa la reserva de un cliente para traer su
            vehiculo a una fecha y hora especifica.
          </p>
        </div>
      </div>

      {/* Creating a Booking */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <span className="text-xs font-medium text-gray-900">Crear una Nueva Cita</span>
        </div>
        <div className={cardStyles.content}>
          <div className="space-y-6">
            {/* Step 1 */}
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-medium">
                  1
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <UserPlus className="h-5 w-5 text-blue-600" />
                  <h4 className="font-semibold text-gray-900">
                    Seleccionar o Crear Cliente
                  </h4>
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  Busca un cliente existente por nombre, telefono o email. Si es
                  un cliente nuevo, pulsa "Crear Nuevo Cliente" e introduce sus
                  datos.
                </p>
                <div className="text-xs bg-amber-50 text-amber-800 p-2 rounded">
                  Consejo: Verifica que el telefono y email sean correctos para
                  enviar notificaciones.
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-medium">
                  2
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Car className="h-5 w-5 text-blue-600" />
                  <h4 className="font-semibold text-gray-900">
                    Seleccionar Vehiculo
                  </h4>
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  Si el cliente tiene vehiculos registrados, selecciona uno de
                  la lista. Si no, crea un nuevo vehiculo con la matricula,
                  marca y modelo.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-medium">
                  3
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="h-5 w-5 text-blue-600" />
                  <h4 className="font-semibold text-gray-900">
                    Tipo de Caso
                  </h4>
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  Selecciona el tipo de cita:
                </p>
                <ul className="text-sm text-gray-600 space-y-1 ml-4">
                  <li>
                    <strong>Particular:</strong> Cliente paga directamente
                  </li>
                  <li>
                    <strong>Seguro:</strong> Reparacion cubierta por aseguradora
                    (requiere datos del seguro)
                  </li>
                  <li>
                    <strong>Garantia:</strong> Cubierto por garantia del
                    fabricante o extendida
                  </li>
                </ul>
              </div>
            </div>

            {/* Step 4 */}
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-medium">
                  4
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="h-5 w-5 text-blue-600" />
                  <h4 className="font-semibold text-gray-900">
                    Fecha y Hora
                  </h4>
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  Selecciona la fecha en el calendario y elige una hora
                  disponible. Los horarios ocupados apareceran en gris.
                </p>
              </div>
            </div>

            {/* Step 5 */}
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-medium">
                  5
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="h-5 w-5 text-blue-600" />
                  <h4 className="font-semibold text-gray-900">Confirmar</h4>
                </div>
                <p className="text-sm text-gray-600">
                  Revisa los datos y pulsa "Crear Cita". El sistema generara una
                  referencia unica y enviara confirmacion al cliente si esta
                  configurado.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Booking States */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <span className="text-xs font-medium text-gray-900">Estados de una Cita</span>
        </div>
        <div className={cardStyles.content}>
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-yellow-50">
              <span className="px-2 py-1 rounded text-xs font-medium bg-yellow-200 text-yellow-800">
                PENDIENTE
              </span>
              <span className="text-sm text-gray-700">
                Esperando confirmacion del cliente
              </span>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50">
              <span className="px-2 py-1 rounded text-xs font-medium bg-green-200 text-green-800">
                CONFIRMADA
              </span>
              <span className="text-sm text-gray-700">
                Cliente ha confirmado la cita
              </span>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-orange-50">
              <span className="px-2 py-1 rounded text-xs font-medium bg-orange-200 text-orange-800">
                REPROGRAMAR
              </span>
              <span className="text-sm text-gray-700">
                Cliente solicito cambio de fecha
              </span>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-red-50">
              <span className="px-2 py-1 rounded text-xs font-medium bg-red-200 text-red-800">
                CANCELADA
              </span>
              <span className="text-sm text-gray-700">
                Cita cancelada por cliente o taller
              </span>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
              <span className="px-2 py-1 rounded text-xs font-medium bg-gray-200 text-gray-800">
                NO SHOW
              </span>
              <span className="text-sm text-gray-700">
                Cliente no se presento
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <span className="text-xs font-medium text-gray-900">Acciones Disponibles</span>
        </div>
        <div className={cardStyles.content}>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="p-4 border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <RefreshCw className="h-5 w-5 text-blue-600" />
                <h4 className="font-medium">Reprogramar</h4>
              </div>
              <p className="text-sm text-gray-600">
                Cambia la fecha y hora de la cita. Se notificara al cliente del
                cambio.
              </p>
            </div>
            <div className="p-4 border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <XCircle className="h-5 w-5 text-red-600" />
                <h4 className="font-medium">Cancelar</h4>
              </div>
              <p className="text-sm text-gray-600">
                Cancela la cita. Requiere seleccionar un motivo de cancelacion.
              </p>
            </div>
            <div className="p-4 border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <h4 className="font-medium">Confirmar</h4>
              </div>
              <p className="text-sm text-gray-600">
                Marca la cita como confirmada manualmente si el cliente confirmo
                por telefono.
              </p>
            </div>
            <div className="p-4 border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="h-5 w-5 text-orange-600" />
                <h4 className="font-medium">Marcar No Show</h4>
              </div>
              <p className="text-sm text-gray-600">
                Indica que el cliente no se presento a la cita programada.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Types */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <span className="text-xs font-medium text-gray-900">Tipos de Reserva y su Flujo</span>
        </div>
        <div className={cardStyles.content}>
          <p className="text-sm text-gray-600 mb-4">
            El tipo de reserva determina el flujo de trabajo que seguira el vehiculo
            una vez llegue al taller. Cada tipo tiene requisitos diferentes.
          </p>
          <div className="space-y-4">
            {/* Particular */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 rounded bg-blue-500">
                  <Users className="h-4 w-4 text-white" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Particular</h4>
                  <p className="text-xs text-gray-500">Cliente paga directamente</p>
                </div>
              </div>
              <div className="flex items-center gap-1 text-[10px] mt-2 flex-wrap">
                <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700">Llegada</span>
                <ArrowRight className="h-3 w-3 text-gray-300" />
                <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700">Recepcion</span>
                <ArrowRight className="h-3 w-3 text-gray-300" />
                <span className="px-2 py-0.5 rounded bg-purple-100 text-purple-700">Valoracion</span>
                <ArrowRight className="h-3 w-3 text-gray-300" />
                <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700">Aprobacion</span>
                <ArrowRight className="h-3 w-3 text-gray-300" />
                <span className="px-2 py-0.5 rounded bg-indigo-100 text-indigo-700">Trabajo</span>
                <ArrowRight className="h-3 w-3 text-gray-300" />
                <span className="px-2 py-0.5 rounded bg-green-100 text-green-700">Entrega</span>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Puede saltar la aprobacion si el presupuesto es menor o ya esta acordado.
              </p>
            </div>

            {/* Seguro */}
            <div className="border rounded-lg p-4 border-orange-200 bg-orange-50/30">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 rounded bg-orange-500">
                  <Shield className="h-4 w-4 text-white" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Seguro</h4>
                  <p className="text-xs text-gray-500">Cubierto por aseguradora</p>
                </div>
              </div>
              <div className="flex items-center gap-1 text-[10px] mt-2 flex-wrap">
                <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700">Llegada</span>
                <ArrowRight className="h-3 w-3 text-gray-300" />
                <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700">Recepcion</span>
                <ArrowRight className="h-3 w-3 text-gray-300" />
                <span className="px-2 py-0.5 rounded bg-purple-100 text-purple-700">Valoracion</span>
                <ArrowRight className="h-3 w-3 text-gray-300" />
                <span className="px-2 py-0.5 rounded bg-orange-100 text-orange-700 ring-2 ring-orange-300 font-medium">Peritacion</span>
                <ArrowRight className="h-3 w-3 text-gray-300" />
                <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700">Aprobacion</span>
                <ArrowRight className="h-3 w-3 text-gray-300" />
                <span className="px-2 py-0.5 rounded bg-indigo-100 text-indigo-700">Trabajo</span>
                <ArrowRight className="h-3 w-3 text-gray-300" />
                <span className="px-2 py-0.5 rounded bg-green-100 text-green-700">Entrega</span>
              </div>
              <p className="text-xs text-orange-700 mt-2 font-medium">
                OBLIGATORIO: Debe pasar por Peritacion antes de Aprobacion.
              </p>
            </div>

            {/* Garantia */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 rounded bg-green-500">
                  <BadgeCheck className="h-4 w-4 text-white" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Garantia</h4>
                  <p className="text-xs text-gray-500">Cubierto por garantia</p>
                </div>
              </div>
              <div className="flex items-center gap-1 text-[10px] mt-2 flex-wrap">
                <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700">Llegada</span>
                <ArrowRight className="h-3 w-3 text-gray-300" />
                <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700">Recepcion</span>
                <ArrowRight className="h-3 w-3 text-gray-300" />
                <span className="px-2 py-0.5 rounded bg-purple-100 text-purple-700">Valoracion</span>
                <ArrowRight className="h-3 w-3 text-gray-300" />
                <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700">Aprobacion</span>
                <ArrowRight className="h-3 w-3 text-gray-300" />
                <span className="px-2 py-0.5 rounded bg-indigo-100 text-indigo-700">Trabajo</span>
                <ArrowRight className="h-3 w-3 text-gray-300" />
                <span className="px-2 py-0.5 rounded bg-green-100 text-green-700">Entrega</span>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Puede incluir Peritacion si la garantia lo requiere.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Booking to Job relationship */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <span className="text-xs font-medium text-gray-900">Relacion Cita → Trabajo</span>
        </div>
        <div className={cardStyles.content}>
          <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-lg mb-4">
            <Calendar className="h-8 w-8 text-blue-600" />
            <ArrowRight className="h-5 w-5 text-blue-400" />
            <Wrench className="h-8 w-8 text-indigo-600" />
          </div>
          <p className="text-sm text-gray-600 mb-3">
            Cuando el vehiculo llega al taller y se marca como <strong>Recepcionado</strong>,
            el sistema crea automaticamente un <strong>Trabajo</strong> asociado.
          </p>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
              <span>La <strong>Cita</strong> representa la reserva/programacion</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
              <span>El <strong>Trabajo</strong> representa el proceso operativo en el taller</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
              <span>El Tablero de Trabajos muestra los trabajos activos por estado</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default BookingFlow;
